import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Table,
  Button,
  DatePicker,
  Select,
  Row,
  Col,
  Statistic,
  Space,
  Typography,
  Spin,
  Alert,
  Tag,
  Progress,
  Divider,
  message,
  Input,
  Form
} from 'antd';
import {
  FileTextOutlined,
  DownloadOutlined,
  CalendarOutlined,
  UserOutlined,
  ProjectOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  BarChartOutlined,
  ExportOutlined,
  FilterOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import AIInsightsPanel from '../components/reports/AIInsightsPanel';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const Reports = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [projectReports, setProjectReports] = useState([]);
  const [employeeReports, setEmployeeReports] = useState([]);
  const [customReports, setCustomReports] = useState([]);
  const [salaryReports, setSalaryReports] = useState(null);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  // Filters
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [groupBy, setGroupBy] = useState('day');
  const [salaryMonth, setSalaryMonth] = useState(dayjs().month() + 1);
  const [salaryYear, setSalaryYear] = useState(dayjs().year());

  useEffect(() => {
    fetchInitialData();
    fetchDashboardSummary();
  }, []);

  const fetchInitialData = async () => {
    try {
      // Fetch projects and employees for filters
      const [projectsRes, employeesRes] = await Promise.all([
        fetch(`${apiUrl}/api/projects`),
        fetch(`${apiUrl}/api/employees`)
      ]);
      
      const projectsData = await projectsRes.json();
      const employeesData = await employeesRes.json();
      
      if (projectsData.success) setProjects(projectsData.data);
      if (employeesData.success) setEmployees(employeesData.data);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const fetchDashboardSummary = async () => {
    try {
      setLoading(true);
      const startDate = dateRange[0]?.format('YYYY-MM-DD');
      const endDate = dateRange[1]?.format('YYYY-MM-DD');
      
      const response = await fetch(
        `${apiUrl}/api/reports/dashboard-summary?startDate=${startDate}&endDate=${endDate}`
      );
      const data = await response.json();
      
      if (data.success) {
        setDashboardData(data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      message.error('Failed to fetch dashboard summary');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectReports = async () => {
    try {
      setLoading(true);
      const startDate = dateRange[0]?.format('YYYY-MM-DD');
      const endDate = dateRange[1]?.format('YYYY-MM-DD');
      const projectId = selectedProject;
      
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (projectId) params.append('projectId', projectId);
      
      const response = await fetch(`${apiUrl}/api/reports/project-wise?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setProjectReports(data.data);
      }
    } catch (error) {
      console.error('Error fetching project reports:', error);
      message.error('Failed to fetch project reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeReports = async () => {
    try {
      setLoading(true);
      const startDate = dateRange[0]?.format('YYYY-MM-DD');
      const endDate = dateRange[1]?.format('YYYY-MM-DD');
      const employeeId = selectedEmployee;
      const department = selectedDepartment;
      
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (employeeId) params.append('employeeId', employeeId);
      if (department) params.append('department', department);
      
      const response = await fetch(`${apiUrl}/api/reports/employee-wise?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setEmployeeReports(data.data);
      }
    } catch (error) {
      console.error('Error fetching employee reports:', error);
      message.error('Failed to fetch employee reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomReports = async () => {
    try {
      setLoading(true);
      const startDate = dateRange[0]?.format('YYYY-MM-DD');
      const endDate = dateRange[1]?.format('YYYY-MM-DD');
      
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      params.append('groupBy', groupBy);
      
      const response = await fetch(`${apiUrl}/api/reports/custom-date-range?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setCustomReports(data.data);
      }
    } catch (error) {
      console.error('Error fetching custom reports:', error);
      message.error('Failed to fetch custom reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalaryReports = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${apiUrl}/api/reports/month-end-salary?month=${salaryMonth}&year=${salaryYear}`
      );
      const data = await response.json();
      
      if (data.success) {
        setSalaryReports(data.data);
      }
    } catch (error) {
      console.error('Error fetching salary reports:', error);
      message.error('Failed to fetch salary reports');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      message.warning('No data to export');
      return;
    }
    
    const headers = Object.keys(data[0]).join(',');
    const csvContent = [
      headers,
      ...data.map(row => Object.values(row).map(val => `"${val}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportToPDF = (data, filename, title, columns) => {
    if (!data || data.length === 0) {
      message.warning('No data to export');
      return;
    }

    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(18);
    doc.text(title, 14, 22);
    
    // Add date range
    doc.setFontSize(12);
    const dateText = `Report Period: ${dateRange[0]?.format('MMM DD, YYYY')} - ${dateRange[1]?.format('MMM DD, YYYY')}`;
    doc.text(dateText, 14, 32);
    
    // Add generation date
    doc.setFontSize(10);
    doc.text(`Generated on: ${dayjs().format('MMM DD, YYYY HH:mm')}`, 14, 40);
    
    // Prepare table data
    const tableColumns = columns.map(col => col.title);
    const tableRows = data.map(row => 
      columns.map(col => {
        const value = row[col.dataIndex];
        if (typeof value === 'object' && value !== null) {
          return JSON.stringify(value);
        }
        return value?.toString() || '';
      })
    );
    
    // Add table
    doc.autoTable({
      head: [tableColumns],
      body: tableRows,
      startY: 50,
      styles: {
        fontSize: 8,
        cellPadding: 3
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      }
    });
    
    // Save the PDF
    doc.save(`${filename}_${dayjs().format('YYYY-MM-DD')}.pdf`);
  };

  const projectColumns = [
    {
      title: 'Project Name',
      dataIndex: 'projectName',
      key: 'projectName',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary">{record.projectCode}</Text>
        </div>
      )
    },
    {
      title: 'Total Hours',
      dataIndex: 'totalHours',
      key: 'totalHours',
      render: (hours) => <Tag color="blue">{hours}h</Tag>,
      sorter: (a, b) => a.totalHours - b.totalHours
    },
    {
      title: 'Team Members',
      dataIndex: 'uniqueEmployeeCount',
      key: 'uniqueEmployeeCount',
      render: (count) => <Tag color="green">{count} members</Tag>
    },
    {
      title: 'Total Entries',
      dataIndex: 'totalEntries',
      key: 'totalEntries'
    },
    {
      title: 'Avg Hours/Entry',
      dataIndex: 'avgHoursPerEntry',
      key: 'avgHoursPerEntry',
      render: (avg) => `${avg?.toFixed(1)}h`
    },
    {
      title: 'Last Activity',
      dataIndex: 'lastActivity',
      key: 'lastActivity',
      render: (date) => dayjs(date).format('MMM DD, YYYY')
    }
  ];

  const employeeColumns = [
    {
      title: 'Employee',
      dataIndex: 'employeeName',
      key: 'employeeName',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary">{record.email}</Text>
          <br />
          <Tag color="purple">{record.department}</Tag>
        </div>
      )
    },
    {
      title: 'Total Hours',
      dataIndex: 'totalHours',
      key: 'totalHours',
      render: (hours) => <Tag color="blue">{hours}h</Tag>,
      sorter: (a, b) => a.totalHours - b.totalHours
    },
    {
      title: 'Projects',
      dataIndex: 'uniqueProjectCount',
      key: 'uniqueProjectCount',
      render: (count) => <Tag color="green">{count} projects</Tag>
    },
    {
      title: 'Total Entries',
      dataIndex: 'totalEntries',
      key: 'totalEntries'
    },
    {
      title: 'Avg Hours/Day',
      dataIndex: 'avgHoursPerEntry',
      key: 'avgHoursPerEntry',
      render: (avg) => `${avg?.toFixed(1)}h`
    },
    {
      title: 'Avg Hours/Week',
      dataIndex: 'avgHoursPerWeek',
      key: 'avgHoursPerWeek',
      render: (avg) => `${avg?.toFixed(1)}h`
    },
    {
      title: 'Last Activity',
      dataIndex: 'lastActivity',
      key: 'lastActivity',
      render: (date) => dayjs(date).format('MMM DD, YYYY')
    }
  ];

  const salaryColumns = [
    {
      title: 'Employee',
      dataIndex: 'employeeName',
      key: 'employeeName',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary">{record.email}</Text>
          <br />
          <Tag color="purple">{record.department}</Tag>
        </div>
      )
    },
    {
      title: 'Total Hours',
      dataIndex: 'totalHours',
      key: 'totalHours',
      render: (hours) => <Tag color="blue">{hours}h</Tag>
    },
    {
      title: 'Regular Hours',
      dataIndex: 'regularHours',
      key: 'regularHours',
      render: (hours) => <Tag color="green">{hours}h</Tag>
    },
    {
      title: 'Overtime Hours',
      dataIndex: 'overtimeHours',
      key: 'overtimeHours',
      render: (hours) => <Tag color="orange">{hours}h</Tag>
    },
    {
      title: 'Working Days',
      dataIndex: 'totalWorkingDays',
      key: 'totalWorkingDays'
    },
    {
      title: 'Total Pay',
      dataIndex: 'totalPay',
      key: 'totalPay',
      render: (pay) => <Text strong>${pay?.toFixed(2)}</Text>
    }
  ];

  const handleTabChange = (key) => {
    setActiveTab(key);
    
    // Fetch data based on the selected tab
    switch (key) {
      case 'dashboard':
        fetchDashboardSummary();
        break;
      case 'projects':
        fetchProjectReports();
        break;
      case 'employees':
        fetchEmployeeReports();
        break;
      case 'custom':
        fetchCustomReports();
        break;
      case 'salary':
        fetchSalaryReports();
        break;
      default:
        break;
    }
  };  

  const tabItems = [
    {
      key: 'dashboard',
      label: (
        <span>
          <BarChartOutlined />
          Dashboard
        </span>
      ),
      children: (
        <Row gutter={[24, 24]}>
          {/* Main Content - 70% */}
          <Col xs={24} xl={17}>
            <Row gutter={[16, 16]} className="mb-4">
              <Col span={24}>
                <Card>
                  <Space>
                    <RangePicker
                      value={dateRange}
                      onChange={setDateRange}
                      format="YYYY-MM-DD"
                    />
                    <Button 
                      type="primary" 
                      onClick={fetchDashboardSummary}
                      loading={loading}
                    >
                      Refresh
                    </Button>
                  </Space>
                </Card>
              </Col>
            </Row>

            {dashboardData && (
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total Hours"
                    value={dashboardData.totalHours}
                    suffix="h"
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total Entries"
                    value={dashboardData.totalEntries}
                    prefix={<FileTextOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Active Employees"
                    value={dashboardData.uniqueEmployeeCount}
                    prefix={<UserOutlined />}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Active Projects"
                    value={dashboardData.uniqueProjectCount}
                    prefix={<ProjectOutlined />}
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card>
                  <Statistic
                    title="Approved Hours"
                    value={dashboardData.approvedHours}
                    suffix="h"
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                  <Progress
                    percent={Math.round((dashboardData.approvedHours / dashboardData.totalHours) * 100)}
                    strokeColor="#52c41a"
                    className="mt-2"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card>
                  <Statistic
                    title="Pending Hours"
                    value={dashboardData.pendingHours}
                    suffix="h"
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: '#faad14' }}
                  />
                  <Progress
                    percent={Math.round((dashboardData.pendingHours / dashboardData.totalHours) * 100)}
                    strokeColor="#faad14"
                    className="mt-2"
                  />
                </Card>
              </Col>
            </Row>
            )}
          </Col>

          {/* AI Insights Panel - 30% */}
          <Col xs={24} xl={7}>
            <AIInsightsPanel 
              insights={dashboardData?.aiInsights}
              type="dashboard"
              specificData={dashboardData ? {
                metrics: [
                  { label: 'Total Hours', value: `${dashboardData.totalHours}h` },
                  { label: 'Active Employees', value: dashboardData.uniqueEmployeeCount },
                  { label: 'Active Projects', value: dashboardData.uniqueProjectCount },
                  { label: 'Approval Rate', value: `${Math.round((dashboardData.approvedHours / dashboardData.totalHours) * 100)}%` }
                ]
              } : null}
            />
          </Col>
        </Row>
      )
    },
    {
      key: 'projects',
      label: (
        <span>
          <ProjectOutlined />
          Project Reports
        </span>
      ),
      children: (
        <Row gutter={[24, 24]}>
          {/* Main Content - 70% */}
          <Col xs={24} xl={17}>
            <Card className="mb-4">
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} md={8}>
                  <RangePicker
                    value={dateRange}
                    onChange={setDateRange}
                    format="YYYY-MM-DD"
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col xs={24} md={6}>
                  <Select
                    placeholder="Select Project"
                    value={selectedProject}
                    onChange={setSelectedProject}
                    allowClear
                    style={{ width: '100%' }}
                  >
                    {projects.map(project => (
                      <Option key={project._id} value={project._id}>
                        {project.name}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} md={10}>
                  <Space>
                    <Button 
                      type="primary" 
                      onClick={fetchProjectReports}
                      loading={loading}
                      icon={<FilterOutlined />}
                    >
                      Generate Report
                    </Button>
                    <Button 
                      onClick={() => exportToCSV(projectReports, 'project_reports')}
                      icon={<DownloadOutlined />}
                    >
                      Export CSV
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Card>

            <Card>
              <Table
                columns={projectColumns}
                dataSource={projectReports}
                rowKey="_id"
                loading={loading}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showTotal: (total) => `Total ${total} projects`
                }}
                onRow={(record) => ({
                  onClick: () => {
                    // Show specific project insights when row is clicked
                  }
                })}
              />
            </Card>
          </Col>

          {/* AI Insights Panel - 30% */}
          <Col xs={24} xl={7}>
            <AIInsightsPanel 
              insights={projectReports.length > 0 && projectReports[0]?.aiInsights}
              type="project"
              specificData={projectReports.length > 0 ? {
                name: projectReports[0]?.projectName || 'All Projects',
                metrics: [
                  { label: 'Total Hours', value: `${projectReports.reduce((sum, p) => sum + (p.totalHours || 0), 0)}h` },
                  { label: 'Projects', value: projectReports.length },
                  { label: 'Avg Hours/Project', value: `${(projectReports.reduce((sum, p) => sum + (p.totalHours || 0), 0) / projectReports.length || 0).toFixed(1)}h` }
                ]
              } : null}
            />
          </Col>
        </Row>
      )
    },
    {
      key: 'employees',
      label: (
        <span>
          <UserOutlined />
          Employee Reports
        </span>
      ),
      children: (
        <Row gutter={[24, 24]}>
          {/* Main Content - 70% */}
          <Col xs={24} xl={17}>
            <Card className="mb-4">
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} md={6}>
                  <RangePicker
                    value={dateRange}
                    onChange={setDateRange}
                    format="YYYY-MM-DD"
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col xs={24} md={5}>
                  <Select
                    placeholder="Select Employee"
                    value={selectedEmployee}
                    onChange={setSelectedEmployee}
                    allowClear
                    style={{ width: '100%' }}
                  >
                    {employees.map(employee => (
                      <Option key={employee._id} value={employee._id}>
                        {employee.name}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} md={5}>
                  <Select
                    placeholder="Select Department"
                    value={selectedDepartment}
                    onChange={setSelectedDepartment}
                    allowClear
                    style={{ width: '100%' }}
                  >
                    <Option value="Engineering">Engineering</Option>
                    <Option value="Design">Design</Option>
                    <Option value="Marketing">Marketing</Option>
                    <Option value="Sales">Sales</Option>
                    <Option value="HR">HR</Option>
                  </Select>
                </Col>
                <Col xs={24} md={8}>
                  <Space>
                    <Button 
                      type="primary" 
                      onClick={fetchEmployeeReports}
                      loading={loading}
                      icon={<FilterOutlined />}
                    >
                      Generate Report
                    </Button>
                    <Button 
                      onClick={() => exportToCSV(employeeReports, 'employee_reports')}
                      icon={<DownloadOutlined />}
                    >
                      Export CSV
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Card>

            <Card>
              <Table
                columns={employeeColumns}
                dataSource={employeeReports}
                rowKey="_id"
                loading={loading}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showTotal: (total) => `Total ${total} employees`
                }}
              />
            </Card>
          </Col>

          {/* AI Insights Panel - 30% */}
          <Col xs={24} xl={7}>
            <AIInsightsPanel 
              insights={employeeReports.length > 0 && employeeReports[0]?.aiInsights}
              type="employee"
              specificData={employeeReports.length > 0 ? {
                name: employeeReports[0]?.employeeName || 'All Employees',
                metrics: [
                  { label: 'Total Hours', value: `${employeeReports.reduce((sum, e) => sum + (e.totalHours || 0), 0)}h` },
                  { label: 'Employees', value: employeeReports.length },
                  { label: 'Avg Hours/Employee', value: `${(employeeReports.reduce((sum, e) => sum + (e.totalHours || 0), 0) / employeeReports.length || 0).toFixed(1)}h` },
                  { label: 'Department', value: employeeReports[0]?.department || 'Multiple' }
                ]
              } : null}
            />
          </Col>
        </Row>
      )
    },
    {
      key: 'custom',
      label: (
        <span>
          <CalendarOutlined />
          Custom Date Reports
        </span>
      ),
      children: (
        <div>
          <Card className="mb-4">
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} md={8}>
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  format="YYYY-MM-DD"
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} md={6}>
                <Select
                  value={groupBy}
                  onChange={setGroupBy}
                  style={{ width: '100%' }}
                >
                  <Option value="day">Group by Day</Option>
                  <Option value="week">Group by Week</Option>
                  <Option value="month">Group by Month</Option>
                </Select>
              </Col>
              <Col xs={24} md={10}>
                <Space>
                  <Button 
                    type="primary" 
                    onClick={fetchCustomReports}
                    loading={loading}
                    icon={<FilterOutlined />}
                  >
                    Generate Report
                  </Button>
                  <Button 
                    onClick={() => exportToCSV(customReports, 'custom_reports')}
                    icon={<DownloadOutlined />}
                  >
                    Export CSV
                  </Button>
                  <Button 
                    onClick={() => exportToPDF(customReports, 'custom_reports', 'Custom Date Range Reports', [
                      { title: 'Period', dataIndex: '_id' },
                      { title: 'Total Hours', dataIndex: 'totalHours' },
                      { title: 'Entries', dataIndex: 'totalEntries' },
                      { title: 'Employees', dataIndex: 'uniqueEmployeeCount' },
                      { title: 'Projects', dataIndex: 'uniqueProjectCount' },
                      { title: 'Avg Hours/Entry', dataIndex: 'avgHoursPerEntry' }
                    ])}
                    icon={<FileTextOutlined />}
                  >
                    Export PDF
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          <Card>
            <Table
              columns={[
                {
                  title: 'Period',
                  dataIndex: '_id',
                  key: '_id'
                },
                {
                  title: 'Total Hours',
                  dataIndex: 'totalHours',
                  key: 'totalHours',
                  render: (hours) => <Tag color="blue">{hours}h</Tag>
                },
                {
                  title: 'Entries',
                  dataIndex: 'totalEntries',
                  key: 'totalEntries'
                },
                {
                  title: 'Employees',
                  dataIndex: 'uniqueEmployeeCount',
                  key: 'uniqueEmployeeCount'
                },
                {
                  title: 'Projects',
                  dataIndex: 'uniqueProjectCount',
                  key: 'uniqueProjectCount'
                },
                {
                  title: 'Avg Hours/Entry',
                  dataIndex: 'avgHoursPerEntry',
                  key: 'avgHoursPerEntry',
                  render: (avg) => `${avg?.toFixed(1)}h`
                }
              ]}
              dataSource={customReports}
              rowKey="_id"
              loading={loading}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total) => `Total ${total} reports`
              }}
            />
          </Card>
        </div>
      )
    },
    {
      key: 'salary',
      label: (
        <span>
          <DollarOutlined />
          Salary Reports
        </span>
      ),
      children: (
        <Row gutter={[24, 24]}>
          {/* Main Content - 70% */}
          <Col xs={24} xl={17}>
            <Card className="mb-4">
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} md={6}>
                  <Select
                    value={salaryMonth}
                    onChange={setSalaryMonth}
                    style={{ width: '100%' }}
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <Option key={i + 1} value={i + 1}>
                        {dayjs().month(i).format('MMMM')}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} md={6}>
                  <Select
                    value={salaryYear}
                    onChange={setSalaryYear}
                    style={{ width: '100%' }}
                  >
                    {Array.from({ length: 5 }, (_, i) => {
                      const year = dayjs().year() - 2 + i;
                      return (
                        <Option key={year} value={year}>
                          {year}
                        </Option>
                      );
                    })}
                  </Select>
                </Col>
                <Col xs={24} md={12}>
                  <Space>
                    <Button 
                      type="primary" 
                      onClick={fetchSalaryReports}
                      loading={loading}
                      icon={<FilterOutlined />}
                    >
                      Generate Salary Report
                    </Button>
                    <Button 
                      onClick={() => salaryReports && exportToCSV(salaryReports.employees, 'salary_reports')}
                      icon={<DownloadOutlined />}
                    >
                      Export CSV
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Card>

            {salaryReports && (
              <>
                <Row gutter={[16, 16]} className="mb-4">
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Total Employees"
                      value={salaryReports.summary.totalEmployees}
                      prefix={<TeamOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Total Hours"
                      value={salaryReports.summary.totalHours}
                      suffix="h"
                      prefix={<ClockCircleOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Overtime Hours"
                      value={salaryReports.summary.totalOvertimeHours}
                      suffix="h"
                      prefix={<ClockCircleOutlined />}
                      valueStyle={{ color: '#fa8c16' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Total Payout"
                      value={salaryReports.summary.totalSalaryPayout}
                      prefix="$"
                      precision={2}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Card>
                </Col>
              </Row>

              <Card>
                <Table
                  columns={salaryColumns}
                  dataSource={salaryReports.employees}
                  rowKey="_id"
                  loading={loading}
                  pagination={{
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total) => `Total ${total} employees`
                  }}
                />
              </Card>
              </>
            )}
          </Col>

          {/* AI Insights Panel - 30% */}
          <Col xs={24} xl={7}>
            <AIInsightsPanel 
              insights={salaryReports?.aiInsights}
              type="salary"
              specificData={salaryReports ? {
                name: `${dayjs().month(salaryMonth - 1).format('MMMM')} ${salaryYear}`,
                metrics: [
                  { label: 'Total Employees', value: salaryReports.summary.totalEmployees },
                  { label: 'Total Hours', value: `${salaryReports.summary.totalHours}h` },
                  { label: 'Overtime Hours', value: `${salaryReports.summary.totalOvertimeHours}h` },
                  { label: 'Total Payout', value: `$${salaryReports.summary.totalSalaryPayout.toFixed(2)}` }
                ]
              } : null}
            />
          </Col>
        </Row>
      )
    }
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <Title level={2}>
          <FileTextOutlined className="mr-2 text-blue-600" />
          Admin Reports
        </Title>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        size="large"
      />
    </div>
  );
};

export default Reports;
